import React from 'react';
import styled from 'styled-components';
import { Bell, ChevronRight, ListTodo, Clock } from 'lucide-react';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { Notification, NotificationType } from '@/common/types/notification';
import { extractDateTime } from '@/common/utils/dateFormat';
import Avatar from '@/common/components/avatar/Avatar';

export interface HistoryItemProps {
  notification: Notification;
  onClick?: () => void;
}

export const HistoryItem: React.FC<HistoryItemProps> = ({ notification, onClick }) => {
  const isClickable = notification.url !== null;
  const isTask = notification.type === NotificationType.TASK;

  const formatContent = (text: string) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <Highlight key={index}>{part.slice(2, -2)}</Highlight>;
      }
      return <React.Fragment key={index}>{part}</React.Fragment>;
    });
  };

  const formatDescription = (text: string) => {
    if (!text) return null;
    return text.split('\n').map((line, index) => (
      <React.Fragment key={index}>
        {index > 0 && <br />}
        {line}
      </React.Fragment>
    ));
  };

  const handleClick = () => {
    if (isClickable && onClick) onClick();
  };

  return (
    <Card $clickable={isClickable} onClick={handleClick}>
      <IconWrapper $type={isTask ? 'TASK' : 'NOTIFICATION'}>
        {isTask ? <ListTodo size={18} /> : <Bell size={18} />}
      </IconWrapper>
      
      <ContentArea>
        <ContentHeader>
          <SenderInfo>
            {notification.sender && (
              <AvatarWrapper>
                <Avatar 
                  src={notification.sender.profileImage} 
                  alt={notification.sender.name} 
                  isUser={true} 
                  sizes='md' 
                />
              </AvatarWrapper>
            )}
            <SenderName>{notification.sender?.name || '시스템 알림'}</SenderName>
            <TypeBadge $isTask={isTask}>
              {isTask ? '할 일' : '알림'}
            </TypeBadge>
          </SenderInfo>
        </ContentHeader>

        <MessageText>{formatContent(notification.message ?? '메시지 없음')}</MessageText>

        {notification.description?.trim() && (
          <DescriptionBox>
            {formatDescription(notification.description)}
          </DescriptionBox>
        )}

        <MetaRow>
          <TimeInfo>
            <Clock size={12} />
            <span>{extractDateTime(new Date(notification.createdAt))}</span>
          </TimeInfo>
          {notification.company && (
            <CompanyBadge>
              {notification.company.name}
              {notification.service && ` · ${notification.service}`}
            </CompanyBadge>
          )}
        </MetaRow>
      </ContentArea>

      {isClickable && (
        <ArrowWrapper>
          <ChevronRight size={18} />
        </ArrowWrapper>
      )}
    </Card>
  );
};

const Card = styled.div<{ $clickable: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: ${semanticColors.background.primary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 14px;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: all 0.2s ease;

  ${props => props.$clickable && `
    &:hover {
      background: ${semanticColors.background.primaryHover};
      border-color: ${semanticColors.border.secondary};
      transform: translateX(4px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
    }
  `}
`;

const IconWrapper = styled.div<{ $type: string }>`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  flex-shrink: 0;
  
  ${props => props.$type === 'TASK' && `
    background: ${semanticColors.background.tertiary};
    color: ${semanticColors.text.brand};
  `}
  
  ${props => props.$type !== 'TASK' && `
    background: ${semanticColors.background.warning.secondary};
    color: ${semanticColors.text.onWarning.primary};
  `}
`;

const ContentArea = styled.div`
  flex: 1;
  min-width: 0;
`;

const ContentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const SenderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AvatarWrapper = styled.div`
  flex-shrink: 0;
`;

const SenderName = styled.span`
  ${typography.subtitle.subtitle2}
  color: ${semanticColors.text.primary};
`;

const TypeBadge = styled.span<{ $isTask: boolean }>`
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  ${typography.caption.caption2}
  background: ${props => props.$isTask 
    ? semanticColors.background.tertiary 
    : semanticColors.background.warning.secondary};
  color: ${props => props.$isTask 
    ? semanticColors.text.brand 
    : semanticColors.text.onWarning.primary};
`;

const MessageText = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.625rem;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Highlight = styled.span`
  color: ${semanticColors.text.brand};
  font-weight: 600;
`;

const DescriptionBox = styled.div`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.secondary};
  background: ${semanticColors.background.secondary};
  padding: 0.75rem 1rem;
  border-radius: 10px;
  margin-bottom: 0.75rem;
  line-height: 1.5;
  border-left: 3px solid ${semanticColors.border.brand};
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const TimeInfo = styled.span`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
`;

const CompanyBadge = styled.span`
  padding: 0.125rem 0.5rem;
  background: ${semanticColors.background.tertiary};
  border-radius: 4px;
  ${typography.caption.caption2}
  color: ${semanticColors.text.brand};
`;

const ArrowWrapper = styled.div`
  color: ${semanticColors.text.tertiary};
  flex-shrink: 0;
  transition: transform 0.2s ease;
  
  ${Card}:hover & {
    transform: translateX(4px);
    color: ${semanticColors.text.brand};
  }
`;

export default HistoryItem;
